import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatEventStatus } from "@altimateai/ui-components/chatbotV2";
import {
  ChatbotProps,
  PlaygroundContext,
  PlaygroundStateProps,
  PlaygroundUserViewEntities,
  Session,
} from "./types";

export const createDefaultSession = (sessionId: string | null = null): Session => ({
  context: {
    user_view: {
      entity_type: PlaygroundUserViewEntities.NONE,
    },
  },
  sessionId,
  chatStatus: "idle",
});

export const initialState: PlaygroundStateProps = {
  sessions: {},
  currentSessionId: null,
  isPlaygroundPopoverOpen: false,
  chatbotProps: {
    initialMessage: undefined,
    helloMessage: undefined,
  },
  forceNewSession: null,
};

const playgroundSlice = createSlice({
  name: "playgroundState",
  initialState,
  reducers: {
    setPlaygroundContext: (
      state,
      action: PayloadAction<{ sessionId: string; context: Partial<PlaygroundContext> }>
    ) => {
      const { sessionId, context } = action.payload;
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = createDefaultSession(sessionId);
      }
      state.sessions[sessionId].context = {
        ...state.sessions[sessionId].context,
        ...context,
      };
    },
    setUserView: (
      state,
      action: PayloadAction<{
        sessionId: string;
        userView: PlaygroundContext["user_view"];
      }>
    ) => {
      const { sessionId, userView } = action.payload;
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = createDefaultSession(sessionId);
      }

      // For one session, this user_view will be set only once
      if (
        state.sessions[sessionId].context.user_view.entity_type === PlaygroundUserViewEntities.NONE
      ) {
        state.sessions[sessionId].context.user_view = userView;
      }
    },
    updateUserView: (
      state,
      action: PayloadAction<{
        sessionId: string;
        userView: Partial<PlaygroundContext["user_view"]>;
      }>
    ) => {
      const { sessionId, userView } = action.payload;
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = createDefaultSession(sessionId);
      }
      state.sessions[sessionId].context.user_view = {
        ...state.sessions[sessionId].context.user_view,
        ...userView,
      };
    },
    setPlaygroundPopoverOpen: (state, action: PayloadAction<boolean>) => {
      state.isPlaygroundPopoverOpen = action.payload;
    },
    setChatbotProps: (state, action: PayloadAction<Partial<ChatbotProps>>) => {
      state.chatbotProps = {
        ...state.chatbotProps,
        ...action.payload,
      };
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = createDefaultSession(sessionId);
      }
      state.currentSessionId = sessionId;
    },
    setCurrentSessionId: (state, action: PayloadAction<string | null>) => {
      state.currentSessionId = action.payload;
    },
    setChatStatus: (
      state,
      action: PayloadAction<{ sessionId: string; chatStatus: ChatEventStatus }>
    ) => {
      const { sessionId, chatStatus } = action.payload;
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = createDefaultSession(sessionId);
      }
      state.sessions[sessionId].chatStatus = chatStatus;
    },
    setForceNewSession: (state, action: PayloadAction<number | null>) => {
      state.forceNewSession = action.payload;
    },
  },
});

export const {
  setPlaygroundContext,
  setUserView,
  updateUserView,
  setPlaygroundPopoverOpen,
  setChatbotProps,
  setSessionId,
  setCurrentSessionId,
  setChatStatus,
  setForceNewSession,
} = playgroundSlice.actions;

export default playgroundSlice;
